import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  Avatar,
  Backdrop,
  Box,
  Button,
  Card,
  CircularProgress,
  Fade,
  IconButton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";

import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import CloseFullscreenIcon from "@mui/icons-material/CloseFullscreen";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import AddCommentOutlinedIcon from "@mui/icons-material/AddCommentOutlined";

import { useAuth } from "../../../../contexts/AuthContext";

import ChatSidebar from "./ChatSidebar";
import Composer from "./Composer";
import MessageList from "./MessageList";
import { useNemoChat } from "./useNemoChat";
import { validateAttachmentFiles } from "./constants";
import { explainNemoError } from "./nemoError";
import {
  AgentId,
  AgentInfo,
  AIProviderName,
  ChatMessage,
  ChatSummary,
} from "./types";

const placeholderSuggestions = [
  "Escrever um e-mail",
  "Gerar um texto",
  "Explicar um conceito",
  "Resumir um conteúdo",
  "Planejar uma tarefa",
  "Tirar uma dúvida",
];

const pickRandomPlaceholder = () =>
  placeholderSuggestions[
    Math.floor(Math.random() * placeholderSuggestions.length)
  ];

const providerToggleSx = {
  px: 1.5,
  py: 0.25,
  fontSize: "0.75rem",
  "&.Mui-selected": {
    bgcolor: "primary.main",
    color: "primary.contrastText",
    "&:hover": {
      bgcolor: "primary.dark",
    },
  },
};

type ChatPaneProps = {
  expanded: boolean;
  nickname: string;
  messages: ChatMessage[];
  sending: boolean;
  draft: string;
  onDraftChange: (value: string) => void;
  files: File[];
  onAddFiles: (files: File[]) => void;
  onRemoveFile: (index: number) => void;
  provider: AIProviderName;
  onProviderChange: (provider: AIProviderName) => void;
  agents: AgentInfo[];
  selectedAgentId: AgentId | null;
  onAgentChange: (agentId: AgentId | null) => void;
  activeAgentId: AgentId | null;
  onSend: () => void;
  onNewChat: () => void;
  placeholder: string;
  attachmentError: string | null;
  historyOpen: boolean;
  loadingChat: boolean;
  onToggleHistory: () => void;
  onToggleExpand: () => void;
  inputRef: React.RefObject<HTMLInputElement>;
};

const ChatPane = ({
  expanded,
  nickname,
  messages,
  sending,
  draft,
  onDraftChange,
  files,
  onAddFiles,
  onRemoveFile,
  provider,
  onProviderChange,
  agents,
  selectedAgentId,
  onAgentChange,
  activeAgentId,
  onSend,
  onNewChat,
  placeholder,
  attachmentError,
  historyOpen,
  loadingChat,
  onToggleHistory,
  onToggleExpand,
  inputRef,
}: ChatPaneProps) => {
  const theme = useTheme();
  const hasConversation = messages.length > 0;

  const activeAgentLabel =
    agents.find((a) => a.id === (activeAgentId ?? "geral"))?.label ?? "Geral";

  return (
    <Stack
      sx={{
        height: "100%",
        minWidth: 0,
        flex: 1,
        px: { xs: 2.5, md: 3 },
        pt: 1.5,
        pb: { xs: 2.5, md: 3 },
      }}
      spacing={1.5}
    >
      <Stack
        direction="row"
        alignItems="center"
        sx={{ flexShrink: 0, position: "relative", minHeight: 32 }}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={0.75}
          sx={{ flex: 1, minWidth: 0 }}
        >
          <Button
            size="small"
            variant={historyOpen ? "contained" : "outlined"}
            color="primary"
            startIcon={<HistoryOutlinedIcon />}
            onClick={onToggleHistory}
            sx={{ px: 1.25, py: 0.25, fontSize: "0.75rem", minWidth: 0 }}
          >
            Histórico
          </Button>
          <Tooltip title="Nova conversa">
            <span>
              <Button
                size="small"
                variant="outlined"
                color="primary"
                startIcon={<AddCommentOutlinedIcon />}
                onClick={onNewChat}
                disabled={sending || loadingChat}
                sx={{ px: 1.25, py: 0.25, fontSize: "0.75rem", minWidth: 0 }}
              >
                Novo chat
              </Button>
            </span>
          </Tooltip>
        </Stack>

        {hasConversation && (
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.75}
            sx={{
              position: "absolute",
              left: "50%",
              transform: "translateX(-50%)",
              pointerEvents: "none",
            }}
          >
            <SmartToyOutlinedIcon
              fontSize="small"
              sx={{ color: "primary.main" }}
            />
            <Typography variant="subtitle2" fontWeight={600} lineHeight={1}>
              Nemo · {activeAgentLabel}
            </Typography>
          </Stack>
        )}

        <Stack
          direction="row"
          alignItems="center"
          spacing={2}
          sx={{ flex: 1, minWidth: 0, justifyContent: "flex-end" }}
        >
          <ToggleButtonGroup
            size="small"
            exclusive
            disabled={hasConversation}
            value={provider}
            onChange={(_, value) => value && onProviderChange(value)}
          >
            <ToggleButton value="gpt" sx={providerToggleSx}>
              GPT
            </ToggleButton>
            <ToggleButton value="claude" sx={providerToggleSx}>
              Claude
            </ToggleButton>
          </ToggleButtonGroup>

          <Tooltip title={expanded ? "Recolher" : "Expandir"}>
            <IconButton onClick={onToggleExpand} size="small" sx={{ p: 0.5 }}>
              {expanded ? (
                <CloseFullscreenIcon fontSize="small" />
              ) : (
                <OpenInFullIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {loadingChat ? (
        <Stack
          alignItems="center"
          justifyContent="center"
          sx={{ flex: 1, minHeight: 0 }}
        >
          <CircularProgress size={28} thickness={4} />
        </Stack>
      ) : (
        <>
          {!hasConversation && (
            <Stack
              alignItems="center"
              justifyContent="center"
              spacing={2}
              sx={{ flex: 1, minHeight: 0, textAlign: "center", px: 1 }}
            >
              <Avatar
                sx={{
                  bgcolor: alpha(theme.palette.primary.main, 0.14),
                  color: "primary.main",
                  width: 48,
                  height: 48,
                }}
              >
                <SmartToyOutlinedIcon />
              </Avatar>
              <Typography variant="h6" fontWeight={600} lineHeight={1.3}>
                Olá, {nickname}, eu sou o Nemo. Como posso ajudar hoje?
              </Typography>
              {agents.length > 0 && (
                <Stack
                  spacing={0.75}
                  alignItems="center"
                  sx={{ width: "100%" }}
                >
                  <Typography variant="caption" color="text.secondary">
                    Modo de especialização
                  </Typography>
                  <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={selectedAgentId ?? "geral"}
                    onChange={(_, value) => {
                      if (value !== null) {
                        onAgentChange(
                          value === "geral" ? null : (value as AgentId),
                        );
                      }
                    }}
                    sx={{ flexWrap: "wrap", justifyContent: "center" }}
                  >
                    <ToggleButton value="geral" sx={providerToggleSx}>
                      Geral
                    </ToggleButton>
                    {agents
                      .filter((a) => a.id !== "geral")
                      .map((agent) => (
                        <ToggleButton
                          key={agent.id}
                          value={agent.id}
                          sx={providerToggleSx}
                        >
                          {agent.label}
                        </ToggleButton>
                      ))}
                  </ToggleButtonGroup>
                </Stack>
              )}
            </Stack>
          )}

          {hasConversation && <MessageList messages={messages} />}
        </>
      )}

      <Composer
        draft={draft}
        onDraftChange={onDraftChange}
        files={files}
        onAddFiles={onAddFiles}
        onRemoveFile={onRemoveFile}
        onSend={onSend}
        sending={sending || loadingChat}
        hasConversation={hasConversation}
        placeholder={placeholder}
        error={attachmentError}
        inputRef={inputRef}
      />
    </Stack>
  );
};

type NemoFrameProps = {
  historyOpen: boolean;
  chats: ChatSummary[];
  activeChatId: string | null;
  onSelectChat: (chatId: string) => void;
  paneProps: ChatPaneProps;
};

const NemoFrame = ({
  historyOpen,
  chats,
  activeChatId,
  onSelectChat,
  paneProps,
}: NemoFrameProps) => (
  <Box sx={{ display: "flex", height: "100%", minWidth: 0 }}>
    <Box
      sx={{
        width: historyOpen ? 240 : 0,
        flexShrink: 0,
        height: "100%",
        overflow: "hidden",
        transition: (t) =>
          t.transitions.create("width", {
            easing: t.transitions.easing.sharp,
            duration: historyOpen
              ? t.transitions.duration.enteringScreen
              : t.transitions.duration.leavingScreen,
          }),
      }}
    >
      <ChatSidebar
        chats={chats}
        activeChatId={activeChatId}
        onSelectChat={onSelectChat}
      />
    </Box>
    <ChatPane {...paneProps} />
  </Box>
);

const Nemo = () => {
  const auth = useAuth();
  const theme = useTheme();
  const nickname = auth?.user?.nickname || "";

  const {
    agents,
    selectedAgentId,
    setSelectedAgentId,
    chats,
    activeChat,
    activeChatId,
    messages,
    sending,
    loadingChat,
    refreshAgents,
    refreshChats,
    openChat,
    startNewChat,
    sendMessage,
  } = useNemoChat();

  const [expanded, setExpanded] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  const [selectedProvider, setSelectedProvider] =
    useState<AIProviderName>("gpt");
  const [placeholder] = useState(() => pickRandomPlaceholder());
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    refreshAgents();
    refreshChats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (activeChat) {
      setSelectedProvider(activeChat.provider);
    }
  }, [activeChat]);

  const handleAddFiles = (newFiles: File[]) => {
    setAttachmentError(null);
    setFiles((prev) => [...prev, ...newFiles]);
  };

  const handleRemoveFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSend = async () => {
    const text = draft.trim();
    if ((!text && files.length === 0) || sending) return;

    const validationError = validateAttachmentFiles(files);
    if (validationError) {
      setAttachmentError(explainNemoError(validationError) || validationError);
      return;
    }

    setAttachmentError(null);
    const filesToSend = files;
    setDraft("");
    setFiles([]);

    const success = await sendMessage(text, filesToSend, selectedProvider);
    if (!success) {
      setDraft(text);
      setFiles(filesToSend);
      setAttachmentError(
        "Não foi possível iniciar a conversa agora. Tente novamente.",
      );
    }
  };

  const handleNewChat = () => {
    setDraft("");
    setFiles([]);
    setAttachmentError(null);
    startNewChat();
  };

  const handleToggleExpand = () => setExpanded((prev) => !prev);
  const handleToggleHistory = () => setHistoryOpen((prev) => !prev);

  useEffect(() => {
    if (!expanded) return undefined;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setExpanded(false);
    };

    document.addEventListener("keydown", handleKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [expanded]);

  const sharedPaneProps: Omit<ChatPaneProps, "expanded"> = {
    nickname,
    messages,
    sending,
    draft,
    onDraftChange: setDraft,
    files,
    onAddFiles: handleAddFiles,
    onRemoveFile: handleRemoveFile,
    provider: selectedProvider,
    onProviderChange: setSelectedProvider,
    agents,
    selectedAgentId,
    onAgentChange: setSelectedAgentId,
    activeAgentId: activeChat?.agentId ?? null,
    onSend: handleSend,
    onNewChat: handleNewChat,
    placeholder,
    attachmentError,
    historyOpen,
    loadingChat,
    onToggleHistory: handleToggleHistory,
    onToggleExpand: handleToggleExpand,
    inputRef,
  };

  const frameProps = {
    historyOpen,
    chats,
    activeChatId,
    onSelectChat: openChat,
  };

  return (
    <>
      <Card
        elevation={0}
        sx={{
          width: "100%",
          height: { xs: 400, md: 450 },
          minWidth: 0,
          overflow: "hidden",
          border: "1px solid",
          borderColor: "primary.main",
          boxShadow: `0 0 14px ${alpha(
            theme.palette.primary.main,
            theme.palette.mode === "light" ? 0.28 : 0.35,
          )}`,
        }}
      >
        <NemoFrame
          {...frameProps}
          paneProps={{ ...sharedPaneProps, expanded: false }}
        />
      </Card>

      {createPortal(
        <>
          <Backdrop
            open={expanded}
            onClick={handleToggleExpand}
            sx={{
              zIndex: (t) => t.zIndex.modal + 1,
              backgroundColor: alpha(theme.palette.common.black, 0.6),
              backdropFilter: "blur(6px)",
              transitionDuration: 250,
            }}
          />
          <Fade in={expanded} timeout={250}>
            <Box
              sx={{
                position: "fixed",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: { xs: "94vw", md: "90vw" },
                height: { xs: "90vh", md: "88vh" },
                zIndex: (t) => t.zIndex.modal + 2,
              }}
            >
              <Card sx={{ height: "100%", boxShadow: 24, overflow: "hidden" }}>
                <NemoFrame
                  {...frameProps}
                  paneProps={{ ...sharedPaneProps, expanded: true }}
                />
              </Card>
            </Box>
          </Fade>
        </>,
        document.body,
      )}
    </>
  );
};

export default Nemo;
